import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, CircularProgress, Typography, Dialog, Button, TextField, Divider,
  AppBar, Toolbar, IconButton, Badge, InputBase, Paper, Chip, Stack,
  Container, Avatar, Menu, MenuItem, Fab, Autocomplete,
  Radio, RadioGroup, FormControl, FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Lottie from 'lottie-react';
import { isMobileDevice, getThemeConfig } from './themes/themeConfig';
import addToCartAnimation from '../../lottie/Addtocart.json';
import ANIMATION_MAP from './animationMap';
import PublicStoreReceipt from './PublicStoreReceipt';
import OrdersDropdown from './OrdersDropdown';

const PublicStorePageEnhanced = () => {
  const { storeSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [cart, setCart] = useState({});
  const [favorites, setFavorites] = useState(new Set());
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [user, setUser] = useState(null);
  const [buyer, setBuyer] = useState({ name: '', phone: '', address: '', areaCode: '', latitude: null, longitude: null });
  const [selectedArea, setSelectedArea] = useState(null);
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [checkingCustomer, setCheckingCustomer] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [deliveryDistance, setDeliveryDistance] = useState(0);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [gpsAutoAttempted, setGpsAutoAttempted] = useState(false);
  const [gpsAutoFailed, setGpsAutoFailed] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderReceipt, setOrderReceipt] = useState(null);
  const [paymentMode, setPaymentMode] = useState('COUNTER');
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [showCartAnimation, setShowCartAnimation] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [lastOrderId, setLastOrderId] = useState(null);
  const [ordersMenuAnchor, setOrdersMenuAnchor] = useState(null);
  const videoRef = useRef(null);
  const cartLottieRef = useRef(null);
  
  // Product animation states
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [animatingProduct, setAnimatingProduct] = useState(null);
  const [visibleAnimations, setVisibleAnimations] = useState(new Set());
  const productRefs = useRef({});
  const cardLottieRef = useRef(null);
  const hydrationRef = useRef(null);

  // Trigger product card animation when it starts animating
  useEffect(() => {
    if (animatingProduct && cardLottieRef.current) {
      cardLottieRef.current.goToAndPlay(0, true);
    }
  }, [animatingProduct]);

  // Trigger floating cart animation when it shows
  useEffect(() => {
    if (showCartAnimation && cartLottieRef.current) {
      cartLottieRef.current.goToAndPlay(0, true);
    }
  }, [showCartAnimation]);

  // Detect device type
  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load user from localStorage
  useEffect(() => {
    const savedUser = localStorage.getItem(`store_user_${storeSlug}`);
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      setBuyer(prev => ({ ...prev, ...parsedUser }));
    }
    const savedFavorites = localStorage.getItem(`store_favorites_${storeSlug}`);
    if (savedFavorites) {
      setFavorites(new Set(JSON.parse(savedFavorites)));
    }
    const savedRecentlyViewed = localStorage.getItem(`store_recent_${storeSlug}`);
    if (savedRecentlyViewed) {
      setRecentlyViewed(JSON.parse(savedRecentlyViewed));
    }
    const savedLastOrderId = localStorage.getItem(`store_last_order_${storeSlug}`);
    if (savedLastOrderId) {
      setLastOrderId(savedLastOrderId);
    }
    const savedCart = localStorage.getItem(`store_cart_${storeSlug}`);
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    hydrationRef.current = storeSlug;
  }, [storeSlug]);

  useEffect(() => {
    if (user) {
      setBuyer(prev => ({ ...prev, ...user }));
    }
  }, [user]);

  // Persist state to localStorage
  useEffect(() => {
    if (storeSlug && hydrationRef.current === storeSlug) {
      if (user) {
        localStorage.setItem(`store_user_${storeSlug}`, JSON.stringify(user));
      } else {
        localStorage.removeItem(`store_user_${storeSlug}`);
      }
    }
  }, [user, storeSlug]);

  useEffect(() => {
    if (storeSlug && hydrationRef.current === storeSlug) {
      localStorage.setItem(`store_recent_${storeSlug}`, JSON.stringify(recentlyViewed));
    }
  }, [recentlyViewed, storeSlug]);

  useEffect(() => {
    if (storeSlug && hydrationRef.current === storeSlug) {
      localStorage.setItem(`store_cart_${storeSlug}`, JSON.stringify(cart));
    }
  }, [cart, storeSlug]);

  useEffect(() => {
    if (storeSlug && hydrationRef.current === storeSlug) {
      localStorage.setItem(`store_favorites_${storeSlug}`, JSON.stringify([...favorites]));
    }
  }, [favorites, storeSlug]);

  // Load store data
  useEffect(() => {
    let mounted = true;

    const loadStore = async () => {
      setLoading(true);
      setError('');
      try {
        const [storeRes, productsRes] = await Promise.all([
          axios.get(`/api/ecommerce-store/public/${storeSlug}`),
          axios.get(`/api/ecommerce-store/public/${storeSlug}/products`)
        ]);

        if (mounted) {
          const storeData = storeRes.data?.data || {};
          console.log('🚚 [DELIVERY] Store data received:', {
            hasDeliverySettings: !!storeData.deliverySettings,
            deliverySettings: storeData.deliverySettings
          });
          
          const productsData = productsRes.data?.data;
          
          // Handle both array and object response formats
          let productsArray = [];
          if (Array.isArray(productsData)) {
            productsArray = productsData;
          } else if (productsData && Array.isArray(productsData.products)) {
            productsArray = productsData.products;
          }
          
          // Log first product to verify tax data
          if (productsArray.length > 0) {
            console.log('🧾 [PRODUCT DATA] First product:', {
              name: productsArray[0].name,
              price: productsArray[0].price,
              taxes: productsArray[0].taxes,
              taxIncluded: productsArray[0].taxIncluded
            });
          }
          
          setStore(storeData);
          setProducts(productsArray);
          setLoading(false);
        }
      } catch (e) {
        console.error('Error loading store:', e);
        if (mounted) {
          setError(e?.response?.data?.message || 'Failed to load store');
          setProducts([]); // Ensure products is always an array
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (storeSlug) loadStore();

    return () => {
      mounted = false;
    };
  }, [storeSlug]);

  useEffect(() => {
    const paySettings = store?.paymentSettings || {};
    const allowCounter = paySettings?.enablePayAtCounter !== false;
    const allowOnline = Boolean(paySettings?.enableOnlinePayment);

    setPaymentMode((prev) => {
      if (prev === 'ONLINE' && allowOnline) return prev;
      if (prev === 'COUNTER' && allowCounter) return prev;
      if (allowCounter) return 'COUNTER';
      if (allowOnline) return 'ONLINE';
      return 'COUNTER';
    });
  }, [store?.paymentSettings]);

  const primaryColor = store?.branding?.primaryColor || '#FC8019';
  const accentColor = store?.branding?.accentColor || '#ff5722';
  const textColor = store?.branding?.textColor || '#111827';
  const backgroundColor = store?.branding?.backgroundColor || '#ffffff';
  const currencySymbol = store?.currency?.symbol || '$';

  // Get theme configuration based on seller's choice
  const themeConfig = useMemo(() => {
    return getThemeConfig(
      store?.branding?.desktopTheme || 'swiggy',
      store?.branding?.mobileTheme || 'swiggy',
      isMobile
    );
  }, [store?.branding, isMobile]);

  const categories = useMemo(() => ['All', ...new Set(products.map(p => p.category || 'Others'))], [products]);
  const bestsellers = useMemo(() => products.filter(p => p.isBestseller || p.salesCount > 10).slice(0, 6), [products]);
  const specialItems = useMemo(() => products.filter(p => p.isSpecial), [products]);
  const comboDeals = useMemo(() => products.filter(p => p.isCombo), [products]);
  const favoritedProducts = useMemo(() => {
    return products.filter(p => favorites.has(String(p.id || p._id)));
  }, [products, favorites]);

  // Derived data
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesCategory = activeCategory === 'All' || (p.category || 'Others') === activeCategory;
      const matchesSearch = !searchQuery || 
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [products, activeCategory, searchQuery]);

  // Separate banners by placement (Matches LiveStorePreview)
  const carouselBanners = useMemo(() => {
    if (!store?.banners || !Array.isArray(store.banners)) return [];
    return store.banners.filter(b => {
      const placement = b.placement || 'top';
      const showInCarousel = b.showInCarousel !== false;
      return showInCarousel && (placement === 'top' || placement === 'Top (Header Carousel)');
    });
  }, [store?.banners]);

  const getBannersForPlacement = (placement) => {
    if (!store?.banners || !Array.isArray(store.banners)) return [];
    return store.banners.filter(b => {
      const bannerPlacement = b.placement || 'top';
      const showInCarousel = b.showInCarousel !== false;
      return bannerPlacement === placement && showInCarousel;
    });
  };

  // Render banner at specific placement (Matches LiveStorePreview exactly)
  const renderPlacementBanner = (placement) => {
    const placementBanners = getBannersForPlacement(placement);
    if (placementBanners.length === 0) return null;

    return (
      <Box sx={{ mb: 3, width: '100%', gridColumn: '1 / -1' }}>
        {placementBanners.map((banner, idx) => (
          <Box
            key={idx}
            sx={{
              height: 120,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#f3f4f6',
              mb: idx < placementBanners.length - 1 ? 2 : 0
            }}
          >
            {banner.type === 'video' ? (
              <video
                src={getImageUrl(banner.url || banner.imageUrl)}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                muted
                autoPlay
                loop
                onError={(e) => { 
                  console.error('Placement banner video failed:', banner.url);
                  e.target.style.display = 'none'; 
                }}
              />
            ) : (
              <img
                src={getImageUrl(banner.url || banner.imageUrl)}
                alt="Banner"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { 
                  console.error('Placement banner image failed:', banner.url);
                  e.target.style.display = 'none'; 
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const autoPlayTimerRef = useRef(null);

  // Auto-advance banner carousel (Matches LiveStorePreview exactly)
  useEffect(() => {
    if (carouselBanners.length <= 1) return;

    const currentBanner = carouselBanners[currentBannerIndex];
    
    // Clear existing timer
    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
    }

    // If current banner is not a video, auto-advance after 5 seconds
    if (currentBanner?.type !== 'video') {
      autoPlayTimerRef.current = setTimeout(() => {
        setCurrentBannerIndex((prev) => (prev + 1) % carouselBanners.length);
      }, 5000);
    }

    return () => {
      if (autoPlayTimerRef.current) {
        clearTimeout(autoPlayTimerRef.current);
      }
    };
  }, [currentBannerIndex, carouselBanners]);

  // Intersection Observer for scroll-triggered animations (Matches LiveStorePreview exactly)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const productId = entry.target.getAttribute('data-product-id');
          if (productId) {
            setVisibleAnimations((prev) => {
              const newSet = new Set(prev);
              if (entry.isIntersecting) {
                newSet.add(String(productId));
              } else {
                newSet.delete(String(productId));
              }
              return newSet;
            });
          }
        });
      },
      { threshold: 0.1 } // Lower threshold for better responsiveness
    );

    // Observe all products currently in the DOM
    const elements = document.querySelectorAll('[data-product-id]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [products, activeCategory, filteredProducts, comboDeals, specialItems]);

  // Banner navigation
  const nextBanner = () => {
    setCurrentBannerIndex((prev) => (prev + 1) % carouselBanners.length);
  };

  const prevBanner = () => {
    setCurrentBannerIndex((prev) => (prev - 1 + carouselBanners.length) % carouselBanners.length);
  };

  const handleVideoEnd = () => {
    nextBanner();
  };

  // Helper to build full image URL (Matches LiveStorePreview exactly)
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    
    // Get base URL and ensure no trailing slash
    let baseURL = axios.defaults.baseURL || window.location.origin;
    if (baseURL.endsWith('/')) {
      baseURL = baseURL.slice(0, -1);
    }
    
    // Ensure path has leading slash
    const path = url.startsWith('/') ? url : `/${url}`;
    
    return `${baseURL}${path}`;
  };

  const triggerCartAnimation = (productId) => {
    setAnimatingProduct(productId);
    setShowCartAnimation(true);
    setTimeout(() => {
      setAnimatingProduct(null);
      setShowCartAnimation(false);
    }, 1000);
  };

  const handleAddToCart = (product) => {
    if (!product) return;
    const productId = String(product.id || product._id);
    triggerCartAnimation(productId);

    setCart(prev => {
      const existing = prev[productId];
      return {
        ...prev,
        [productId]: { 
          ...product, 
          qty: (existing?.qty || 0) + 1, 
          price: product.price 
        }
      };
    });
    
    addToRecentlyViewed(product);
  };

  const handleIncrement = (product) => {
    if (!product) return;
    const productId = String(product.id || product._id);
    triggerCartAnimation(productId);

    setCart(prev => {
      const existing = prev[productId];
      return {
        ...prev,
        [productId]: { 
          ...product, 
          qty: (existing?.qty || 0) + 1, 
          price: product.price 
        }
      };
    });
  };

  const handleDecrement = (product) => {
    const productId = String(product.id || product._id);
    setCart(prev => {
      const newCart = { ...prev };
      if (newCart[productId]?.qty > 1) {
        newCart[productId] = { ...newCart[productId], qty: newCart[productId].qty - 1 };
      } else {
        delete newCart[productId];
      }
      return newCart;
    });
  };

  // Computed values
  const cartCount = Object.values(cart).reduce((sum, item) => sum + (item?.qty || 0), 0);
  
  // Calculate subtotal and tax - same logic as BillingNew
  const calculateCartTotals = () => {
    let subtotalWithoutTax = 0;
    let inclusiveTaxTotal = 0;
    let exclusiveTaxTotal = 0;
    
    Object.values(cart).forEach(item => {
      const qty = item?.qty || 0;
      const price = item?.price || 0;
      const itemTotal = qty * price;
      const taxArray = item?.taxes || [];
      
      console.log('🧾 [TAX CALC] Item:', {
        name: item?.name,
        qty,
        price,
        itemTotal,
        taxes: taxArray,
        taxIncluded: item?.taxIncluded
      });
      
      // Check if product has taxes
      if (taxArray.length > 0) {
        // Inclusive tax logic (tax included in price)
        if (item?.taxIncluded === true || item?.taxIncluded === 'true') {
          const totalInclTax = taxArray.reduce((sum, tax) => {
            const rate = tax?.taxPercentage || tax?.amount || 0;
            return sum + (itemTotal * rate) / (100 + rate);
          }, 0);
          
          subtotalWithoutTax += itemTotal - totalInclTax;
          inclusiveTaxTotal += totalInclTax;
          
          console.log('🧾 [TAX CALC] Inclusive tax:', {
            itemTotal,
            totalInclTax,
            subtotalForItem: itemTotal - totalInclTax
          });
        } 
        // Exclusive tax logic (tax added on top)
        else if (item?.taxIncluded === false || item?.taxIncluded === 'false') {
          const totalExclTax = taxArray.reduce((sum, tax) => {
            const rate = tax?.taxPercentage || tax?.amount || 0;
            return sum + (itemTotal * rate) / 100;
          }, 0);
          
          subtotalWithoutTax += itemTotal;
          exclusiveTaxTotal += totalExclTax;
          
          console.log('🧾 [TAX CALC] Exclusive tax:', {
            itemTotal,
            totalExclTax,
            subtotalForItem: itemTotal
          });
        } else {
          // No tax info - treat as no tax
          subtotalWithoutTax += itemTotal;
        }
      } else {
        // No tax on this item
        subtotalWithoutTax += itemTotal;
      }
    });
    
    const totalTax = inclusiveTaxTotal + exclusiveTaxTotal;
    
    console.log('🧾 [TAX CALC] Totals:', {
      subtotalWithoutTax,
      inclusiveTaxTotal,
      exclusiveTaxTotal,
      totalTax
    });
    
    return { 
      subtotal: subtotalWithoutTax, 
      taxAmount: totalTax,
      inclusiveTax: inclusiveTaxTotal,
      exclusiveTax: exclusiveTaxTotal
    };
  };
  
  const { subtotal: cartSubtotal, taxAmount: cartTax } = calculateCartTotals();
  // Calculate distance between two GPS coordinates using Haversine formula
  const calculateGPSDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return Math.round(distance * 10) / 10; // Round to 1 decimal
  };

  // Get customer's current GPS location
  const getCustomerLocation = (isAutomatic = false) => {
    if (!navigator.geolocation) {
      console.log('📍 [GPS] Geolocation not supported');
      if (!isAutomatic) {
        alert('Geolocation is not supported by your browser');
      }
      if (isAutomatic) {
        setGpsAutoFailed(true);
      }
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const customerLat = position.coords.latitude;
        const customerLon = position.coords.longitude;
        
        console.log('📍 [GPS] Customer location:', { customerLat, customerLon, isAutomatic });
        
        // Update buyer with GPS coordinates
        setBuyer(prev => ({
          ...prev,
          latitude: customerLat,
          longitude: customerLon
        }));
        
        // Find store location from selected area or use first area with GPS
        const selectedAreaData = store?.deliveryAreas?.find(a => a.areaCode === buyer.areaCode);
        const storeArea = selectedAreaData || store?.deliveryAreas?.find(a => a.latitude && a.longitude);
        
        if (storeArea?.latitude && storeArea?.longitude) {
          const storeLat = Number(storeArea.latitude);
          const storeLon = Number(storeArea.longitude);
          
          console.log('📍 [GPS] Store location:', { storeLat, storeLon, areaName: storeArea.areaName });
          
          // Calculate distance
          const distance = calculateGPSDistance(customerLat, customerLon, storeLat, storeLon);
          console.log('📍 [GPS] Calculated distance:', distance, 'KM');
          
          setDeliveryDistance(distance);
          setGpsAutoFailed(false);
        } else {
          console.log('📍 [GPS] Store location not available');
          if (!isAutomatic) {
            alert('Store location GPS coordinates not available. Please contact the store.');
          }
          if (isAutomatic) {
            setGpsAutoFailed(true);
          }
        }
        
        setGettingLocation(false);
      },
      (error) => {
        console.error('📍 [GPS] Error getting location:', error.message);
        if (!isAutomatic) {
          alert('Unable to get your location. Please enable location services.');
        }
        if (isAutomatic) {
          setGpsAutoFailed(true);
        }
        setGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const cartTotal = cartSubtotal + cartTax + deliveryCharge;

  // Calculate delivery charge based on settings
  const calculateDeliveryCharge = () => {
    const settings = store?.deliverySettings;
    console.log('🚚 [DELIVERY CALC] Starting calculation:', {
      hasSettings: !!settings,
      enabled: settings?.enabled,
      mode: settings?.mode,
      flatAmount: settings?.flatAmount,
      perKmCharge: settings?.perKmCharge,
      deliveryDistance,
      cartSubtotal,
      freeDeliveryAbove: settings?.freeDeliveryAbove
    });
    
    if (!settings || !settings.enabled) {
      console.log('🚚 [DELIVERY CALC] Delivery not enabled, setting to 0');
      setDeliveryCharge(0);
      return;
    }

    const mode = settings.mode || 'flat';
    
    // Check free delivery threshold
    if (settings.freeDeliveryAbove > 0 && cartSubtotal >= settings.freeDeliveryAbove) {
      console.log('🚚 [DELIVERY CALC] Free delivery threshold met, setting to 0');
      setDeliveryCharge(0);
      return;
    }

    if (mode === 'flat') {
      const charge = Number(settings.flatAmount || 0);
      console.log('🚚 [DELIVERY CALC] Flat mode, setting charge to:', charge);
      setDeliveryCharge(charge);
    } else if (mode === 'per_km') {
      const distance = Number(deliveryDistance || 0);
      const perKm = Number(settings.perKmCharge || 0);
      const charge = distance * perKm;
      console.log('🚚 [DELIVERY CALC] Per KM mode:', { distance, perKm, charge });
      setDeliveryCharge(charge);
    } else if (mode === 'km_range') {
      const distance = Number(deliveryDistance || 0);
      const ranges = settings.kmRanges || [];
      let charge = 0;
      
      // Find matching range
      for (const range of ranges) {
        const from = Number(range.fromKm || 0);
        const to = Number(range.toKm || 999999);
        if (distance >= from && distance <= to) {
          charge = Number(range.charge || 0);
          console.log('🚚 [DELIVERY CALC] KM Range matched:', { distance, from, to, charge });
          break;
        }
      }
      
      console.log('🚚 [DELIVERY CALC] KM Range mode:', { distance, ranges: ranges.length, charge });
      setDeliveryCharge(charge);
    }
  };

  // Auto-trigger GPS detection when checkout opens
  useEffect(() => {
    if (checkoutOpen && !gpsAutoAttempted && store?.deliverySettings?.enabled && 
        (store?.deliverySettings?.mode === 'per_km' || store?.deliverySettings?.mode === 'km_range')) {
      console.log('📍 [GPS AUTO] Attempting automatic GPS detection on checkout open');
      setGpsAutoAttempted(true);
      getCustomerLocation(true);
    }
  }, [checkoutOpen]);

  // Recalculate delivery charge when cart, settings, or distance change
  useEffect(() => {
    console.log('🚚 [DELIVERY EFFECT] Effect triggered:', {
      hasStore: !!store,
      hasDeliverySettings: !!store?.deliverySettings,
      cartSubtotal,
      deliveryDistance
    });
    if (store?.deliverySettings) {
      calculateDeliveryCharge();
    }
  }, [cartSubtotal, store?.deliverySettings, deliveryDistance]);

  // Handle order placement
  const handlePlaceOrder = async () => {
    if (Object.keys(cart).length === 0) return;
    if (!buyer.name || !buyer.phone || !buyer.areaCode) {
      alert('Please fill in required details (Name, Phone, Area)');
      return;
    }

    const paySettings = store?.paymentSettings || {};
    const allowCounter = paySettings?.enablePayAtCounter !== false;
    const allowOnline = Boolean(paySettings?.enableOnlinePayment);
    const safePaymentMode = paymentMode === 'ONLINE' ? 'ONLINE' : 'COUNTER';
    if (safePaymentMode === 'ONLINE' && !allowOnline) {
      alert('Online payment is not enabled for this store');
      return;
    }
    if (safePaymentMode === 'COUNTER' && !allowCounter) {
      alert('Pay at counter is not enabled for this store');
      return;
    }
    
    setPlacingOrder(true);
    try {
      const orderData = {
        areaCode: buyer.areaCode,
        customer: {
          name: buyer.name,
          phone: buyer.phone,
          address: buyer.address,
          latitude: buyer.latitude,
          longitude: buyer.longitude
        },
        paymentMode: safePaymentMode,
        deliveryCharge: deliveryCharge,
        items: Object.values(cart).map(item => ({
          productId: item.id || item._id,
          qty: item.qty,
          price: item.price
        }))
      };

      const res = await axios.post(`/api/ecommerce-store/public/${storeSlug}/order`, orderData);
      
      if (res.data?.success) {
        if (safePaymentMode === 'ONLINE') {
          // Placeholder for gateway integration. Backend will later return payment session/redirect.
          alert('Online payment is not integrated yet. Please use Pay at Counter for now.');
          return;
        }
        
        // Prepare receipt data
        const receiptData = {
          orderId: res.data.data?.orderId,
          subtotal: cartSubtotal,
          tax: cartTax,
          deliveryCharge: deliveryCharge,
          total: res.data.data?.total || cartTotal,
          items: Object.values(cart).map(item => ({
            name: item.name,
            qty: item.qty,
            price: item.price,
            lineTotal: item.qty * item.price
          })),
          customer: {
            name: buyer.name,
            phone: buyer.phone,
            address: buyer.address
          },
          paymentMode: safePaymentMode
        };
        
        // Save last order ID for track order icon
        const orderId = res.data.data?.orderId;
        if (orderId) {
          setLastOrderId(orderId);
          localStorage.setItem(`store_last_order_${storeSlug}`, orderId);
        }
        
        setOrderReceipt(receiptData);
        setOrderSuccess(true);
        setCart({});
        localStorage.setItem(`store_cart_${storeSlug}`, JSON.stringify({}));
        setCheckoutOpen(false);
      } else {
        alert(res.data?.message || 'Failed to place order');
      }
    } catch (e) {
      console.error('Order placement error:', e);
      alert(e.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setPlacingOrder(false);
    }
  };

  // Favorites
  const toggleFavorite = (productId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(productId)) {
        newFavorites.delete(productId);
      } else {
        newFavorites.add(productId);
      }
      return newFavorites;
    });
  };

  // Recently viewed
  const addToRecentlyViewed = (product) => {
    setRecentlyViewed(prev => {
      const productId = String(product.id || product._id);
      const filtered = prev.filter(p => String(p.id || p._id) !== productId);
      const updated = [product, ...filtered].slice(0, 10);
      return updated;
    });
  };

  // Check if customer exists by phone
  const checkExistingCustomer = async (phone) => {
    if (!phone || phone.length < 10) {
      setExistingCustomer(null);
      return;
    }

    try {
      setCheckingCustomer(true);
      const response = await axios.get(`/api/ecommerce-store/public/${storeSlug}/customer/${phone}`);
      
      if (response.data?.success && response.data?.exists) {
        const customerData = response.data.customer;
        setExistingCustomer({
          ...customerData,
          orderCount: response.data.orderCount,
          recentOrders: response.data.recentOrders
        });
        
        // Auto-fill customer data
        setBuyer(prev => ({
          ...prev,
          name: customerData.name || prev.name,
          address: customerData.address || prev.address
        }));
      } else {
        setExistingCustomer(null);
      }
    } catch (error) {
      console.error('Error checking customer:', error);
      setExistingCustomer(null);
    } finally {
      setCheckingCustomer(false);
    }
  };

  // User login/register
  const handleLogin = () => {
    if (buyer.name && buyer.phone && buyer.areaCode) {
      const userData = { ...buyer, registeredAt: new Date().toISOString() };
      setUser(userData);
      setLoginOpen(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem(`store_user_${storeSlug}`);
    setUserMenuAnchor(null);
    setBuyer({ name: '', phone: '', address: '', areaCode: '' });
  };

  // Inline Banner Component
  const InlineBanner = ({ banner }) => {
    if (!banner) return null;
    
    const bannerUrl = banner.url || banner.imageUrl;
    if (!bannerUrl) return null;

    return (
      <Box
        sx={{
          width: '100%',
          height: isMobile ? '150px' : '250px',
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <img
          src={getImageUrl(bannerUrl)}
          alt={`Banner - ${banner.placement}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          onError={(e) => {
            console.error('❌ Inline banner failed:', banner.placement, bannerUrl);
            e.target.style.display = 'none';
          }}
        />
      </Box>
    );
  };

  // Product Card Renderer (Matches LiveStorePreview exactly)
  const renderProductCard = (product) => {
    if (!product) return null;
    const { productCard } = themeConfig;
    const isHorizontal = productCard.style === 'horizontal' || productCard.style === 'wide' || 
                         productCard.style === 'classic' || productCard.style === 'mobile-list';
    
    const isCombo = Boolean(product.isCombo);
    const isSpecial = Boolean(product.isSpecial);
    const hasProductList = Array.isArray(product.productList) && product.productList.length > 0;
    const productId = String(product.id || product._id);
    const isHovered = hoveredProduct === productId;
    const isAnimating = animatingProduct === productId;
    const qty = cart[productId]?.qty || 0;
    const isFavorite = favorites.has(productId);
    
    // Product animation settings
    const hasAnimation = product.animation?.enabled && product.animation?.file;
    const animationData = hasAnimation ? ANIMATION_MAP[product.animation.file] : null;
    const showProductAnimation = hasAnimation && animationData && (
      product.animation.trigger === 'always' ||
      (product.animation.trigger === 'hover' && isHovered) ||
      (product.animation.trigger === 'scroll' && visibleAnimations.has(productId))
    );

    return (
      <Paper
        key={productId}
        data-product-id={productId}
        ref={(el) => { if (el) productRefs.current[productId] = el; }}
        onMouseEnter={() => setHoveredProduct(productId)}
        onMouseLeave={() => setHoveredProduct(null)}
        onClick={() => addToRecentlyViewed(product)}
        sx={{
          borderRadius: `${productCard.borderRadius}px`,
          boxShadow: productCard.shadow || 'none',
          border: productCard.border || 'none',
          padding: `${productCard.padding}px`,
          display: 'flex',
          flexDirection: isHorizontal ? 'row' : 'column',
          gap: isHorizontal ? 2 : 1,
          height: '100%',
          position: 'relative',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }
        }}
      >
        {/* Favorite Button */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(productId);
          }}
          sx={{
            position: 'absolute',
            top: (isCombo || isSpecial) ? 40 : 8,
            right: 8,
            bgcolor: 'rgba(255,255,255,0.8)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 10,
            width: 32,
            height: 32,
            color: isFavorite ? '#d32f2f' : '#666',
            '&:hover': { 
              bgcolor: 'rgba(255,255,255,1)',
              color: isFavorite ? '#b71c1c' : '#333'
            }
          }}
        >
          {isFavorite ? <FavoriteIcon sx={{ fontSize: 18 }} /> : <FavoriteBorderIcon sx={{ fontSize: 18 }} />}
        </IconButton>

        {/* Product Animation Overlay */}
        {showProductAnimation && animationData && (
          <Box
            sx={{
              position: 'absolute',
              ...(product.animation.position === 'top-left' && { top: 8, left: 8 }),
              ...(product.animation.position === 'top-right' && { top: 8, right: 8 }),
              ...(product.animation.position === 'bottom-left' && { bottom: 8, left: 8 }),
              ...(product.animation.position === 'bottom-right' && { bottom: 8, right: 8 }),
              ...(product.animation.position === 'center-overlay' && { 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)' 
              }),
              width: product.animation.size || 80,
              height: product.animation.size || 80,
              zIndex: 5,
              pointerEvents: 'none'
            }}
          >
            <Lottie
              animationData={animationData}
              loop={true}
              autoplay={true}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        )}

        {/* Special/Combo Badge */}
        {(isCombo || isSpecial) && (
          <Chip
            label={isCombo ? 'COMBO' : 'SPECIAL'}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: isCombo ? '#ff9800' : '#e91e63',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 20,
              zIndex: 1
            }}
          />
        )}
        
        {/* Image */}
        <Box
          sx={{
            width: isHorizontal ? `${productCard.imageWidth || 100}px` : '100%',
            height: `${productCard.imageHeight}px`,
            borderRadius: `${productCard.borderRadius - 4}px`,
            bgcolor: '#f3f4f6',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {product.imageUrl ? (
            <img 
              src={getImageUrl(product.imageUrl)} 
              alt={product.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { 
                console.error('Product image failed to load:', product.imageUrl);
                e.target.style.display = 'none'; 
                const parent = e.target.parentElement;
                if (parent) {
                  parent.innerHTML = '<span style="font-size:0.75rem;color:#999">IMG</span>';
                }
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">IMG</Typography>
          )}
          {/* Veg indicator */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              width: 18,
              height: 18,
              border: `2px solid ${product.isVeg ? '#0f8a0f' : '#e43b4f'}`,
              borderRadius: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.95)'
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: product.isVeg ? '#0f8a0f' : '#e43b4f'
              }}
            />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
            {product.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {product.category}
          </Typography>
          
          {/* Combo/Special Product List */}
          {hasProductList && (
            <Box sx={{ mb: 1, p: 1, bgcolor: '#f9fafb', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {isCombo ? 'Includes:' : 'Items:'}
              </Typography>
              <Stack spacing={0.25}>
                {product.productList.slice(0, 3).map((item, idx) => (
                  <Typography key={idx} variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    • {item.productName || 'Item'} {item.qty ? `(${item.qty})` : ''}
                  </Typography>
                ))}
                {product.productList.length > 3 && (
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: primaryColor, fontWeight: 600 }}>
                    +{product.productList.length - 3} more items
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="body2" fontWeight={800}>
                {currencySymbol} {product.price?.toFixed(2)}
              </Typography>
              {product.mrp > product.price && (
                <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '0.7rem' }}>
                  {currencySymbol} {product.mrp?.toFixed(2)}
                </Typography>
              )}
            </Box>
            
            {/* Show quantity controls if item is in cart, otherwise show ADD button */}
            {qty > 0 ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDecrement(product);
                  }}
                  sx={{ bgcolor: primaryColor, color: '#fff', width: 24, height: 24, '&:hover': { bgcolor: primaryColor } }}
                >
                  <RemoveIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 24, textAlign: 'center', color: primaryColor }}>
                  {qty}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleIncrement(product);
                  }}
                  sx={{ bgcolor: primaryColor, color: '#fff', width: 24, height: 24, '&:hover': { bgcolor: primaryColor } }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>
            ) : (isHovered || isAnimating) ? (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart(product);
                }}
                sx={{
                  width: 60,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <Lottie
                  lottieRef={isAnimating ? cardLottieRef : undefined}
                  animationData={addToCartAnimation}
                  loop={isAnimating}
                  autoplay={true}
                  style={{ width: 60, height: 60 }}
                />
              </Box>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart(product);
                }}
                sx={{
                  borderColor: primaryColor,
                  color: primaryColor,
                  fontSize: '0.75rem',
                  minWidth: 60,
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: primaryColor,
                    bgcolor: `${primaryColor}10`
                  }
                }}
              >
                ADD
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderCart = () => {
    const { cart: cartConfig } = themeConfig;
    if (!cartConfig) return null;
    
    // Side cart for desktop
    if (!isMobile && (cartConfig.position === 'side' || cartConfig.position === 'side-panel' || cartConfig.position === 'elegant-side' || cartConfig.position === 'fixed-right')) {
      return (
        <Box sx={{ width: '100%', maxWidth: 350, flexShrink: 0 }}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 4,
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
              bgcolor: '#ffffff',
              height: 'fit-content',
              position: 'sticky',
              top: 100,
              border: '1px solid #f0f0f0'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" fontWeight={900} sx={{ letterSpacing: '-0.5px' }}>
                Your Cart
              </Typography>
              <Chip 
                label={`${cartCount} Items`} 
                size="small" 
                sx={{ bgcolor: `${primaryColor}15`, color: primaryColor, fontWeight: 700, borderRadius: 1.5 }} 
              />
            </Box>
            
            {cartCount === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box sx={{ width: 180, height: 180, mx: 'auto', mb: 2, filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.05))' }}>
                  <Lottie
                    animationData={ANIMATION_MAP['Order.json']}
                    loop={true}
                    autoplay={true}
                  />
                </Box>
                <Typography variant="h6" fontWeight={800} color="text.primary" gutterBottom>
                  Empty Cart
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                  Looks like you haven't added anything to your cart yet.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Stack spacing={2.5} sx={{ mb: 3, maxHeight: '55vh', overflowY: 'auto', pr: 1, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#e0e0e0', borderRadius: 2 } }}>
                  {Object.values(cart).map((item) => (
                    <Box key={item.id || item._id} sx={{ display: 'flex', gap: 2, position: 'relative' }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 2,
                          bgcolor: '#f8f9fa',
                          overflow: 'hidden',
                          flexShrink: 0,
                          border: '1px solid #eee'
                        }}
                      >
                        <img
                          src={getImageUrl(item.imageUrl)}
                          alt={item.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ mb: 0.25 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {currencySymbol} {item.price?.toFixed(2)}
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f0f2f5', borderRadius: 2, px: 0.5, py: 0.25 }}>
                            <IconButton size="small" onClick={() => handleDecrement(item)} sx={{ p: 0.5, color: '#555' }}>
                              <RemoveIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <Typography variant="caption" fontWeight={800} sx={{ minWidth: 20, textAlign: 'center' }}>
                              {item.qty}
                            </Typography>
                            <IconButton size="small" onClick={() => handleIncrement(item)} sx={{ p: 0.5, color: primaryColor }}>
                              <AddIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        </Stack>
                      </Box>
                      <Typography variant="body2" fontWeight={800} sx={{ color: '#1a1a1a', mt: 0.5 }}>
                        {currencySymbol} {(item.price * item.qty).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                
                <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 3, p: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Subtotal</Typography>
                    <Typography variant="caption" fontWeight={700}>{currencySymbol}{cartSubtotal.toFixed(2)}</Typography>
                  </Box>
                  {cartTax > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Tax</Typography>
                      <Typography variant="caption" fontWeight={700}>{currencySymbol}{cartTax.toFixed(2)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Delivery Fee</Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color: deliveryCharge === 0 ? '#2e7d32' : 'text.primary' }}>
                      {deliveryCharge === 0 ? 'FREE' : `${currencySymbol}${deliveryCharge.toFixed(2)}`}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5, borderStyle: 'dashed' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" fontWeight={800}>Total Amount</Typography>
                    <Typography variant="body1" fontWeight={900} sx={{ color: primaryColor }}>
                      {currencySymbol}{cartTotal.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    if (!user) {
                      setLoginOpen(true);
                    } else {
                      setCheckoutOpen(true);
                    }
                  }}
                  endIcon={<NavigateNextIcon />}
                  sx={{
                    bgcolor: primaryColor,
                    color: 'white',
                    fontWeight: 800,
                    py: 1.8,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: `0 8px 20px ${primaryColor}40`,
                    '&:hover': { 
                      bgcolor: primaryColor,
                      boxShadow: `0 12px 25px ${primaryColor}60`,
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  {user ? 'Proceed to Checkout' : 'Login to Order'}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      );
    }

    // Bottom bar for mobile
    if (isMobile && (cartConfig.type === 'sticky-bar' || cartConfig.type === 'fixed-bottom' || cartConfig.position === 'sticky-bottom')) {
      if (cartCount === 0) return null;
      return (
        <Paper
          elevation={10}
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 16,
            p: 1.5,
            bgcolor: primaryColor,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 1100,
            borderRadius: 4,
            backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
            boxShadow: `0 10px 30px ${primaryColor}60`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Badge badgeContent={cartCount} color="error" sx={{ '& .MuiBadge-badge': { bgcolor: 'white', color: primaryColor, fontWeight: 900 } }}>
              <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
                <ShoppingCartIcon sx={{ fontSize: 20 }} />
              </Box>
            </Badge>
            <Box>
              <Typography variant="caption" fontWeight={700} sx={{ opacity: 0.9, display: 'block', lineHeight: 1 }}>
                Total Amount
              </Typography>
              <Typography variant="body1" fontWeight={900}>
                {currencySymbol} {cartTotal.toFixed(2)}
              </Typography>
            </Box>
          </Box>
          <Button
            onClick={() => {
              if (!user) {
                setLoginOpen(true);
              } else {
                setCheckoutOpen(true);
              }
            }}
            endIcon={<NavigateNextIcon />}
            sx={{
              color: 'white',
              fontWeight: 800,
              textTransform: 'none',
              fontSize: '0.9rem',
              bgcolor: 'rgba(255,255,255,0.15)',
              px: 2,
              borderRadius: 2.5,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
            }}
          >
            {user ? 'View Cart' : 'Login to Order'}
          </Button>
        </Paper>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: primaryColor }} />
      </Box>
    );
  }

  if (error || !store) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Typography variant="h6" color="error">{error || 'Store not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: backgroundColor, minHeight: '100vh' }}>
      {/* Top Navigation Bar */}
      <AppBar position="sticky" sx={{ bgcolor: 'white', color: 'black', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Toolbar>
          {/* Logo/Store Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 0, mr: 3 }}>
            {store.branding?.logoUrl && (
              <Box
                component="img"
                src={getImageUrl(store.branding.logoUrl)}
                alt={store.storeName}
                sx={{
                  height: 40,
                  width: 'auto',
                  mr: 1.5,
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  console.error('❌ Logo failed to load:', store.branding.logoUrl);
                  e.target.style.display = 'none';
                }}
              />
            )}
            <Typography variant="h6" fontWeight={700} sx={{ color: primaryColor }}>
              {store.storeName}
            </Typography>
          </Box>

          {/* Search Bar */}
          <Paper
            sx={{
              flexGrow: 1,
              maxWidth: 600,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 0.5,
              bgcolor: '#f5f5f5',
              boxShadow: 'none',
              border: '1px solid #e0e0e0'
            }}
          >
            <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
            <InputBase
              placeholder="Search for dishes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
          </Paper>

          <Box sx={{ flexGrow: 1 }} />

          {/* Orders Dropdown */}
          {user && (
            <IconButton 
              onClick={(e) => setOrdersMenuAnchor(e.currentTarget)} 
              sx={{ ml: 1 }}
              title="My Orders"
            >
              <Badge 
                badgeContent={lastOrderId ? '•' : 0} 
                color="success"
                variant="dot"
              >
                <ReceiptIcon />
              </Badge>
            </IconButton>
          )}

          {/* User Menu */}
          {user ? (
            <>
              <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: primaryColor }}>
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={() => setUserMenuAnchor(null)}
              >
                <MenuItem disabled>
                  <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                </MenuItem>
                <MenuItem disabled>
                  <Typography variant="caption">{user.phone}</Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
              
              {/* Orders Dropdown */}
              <OrdersDropdown
                anchorEl={ordersMenuAnchor}
                open={Boolean(ordersMenuAnchor)}
                onClose={() => setOrdersMenuAnchor(null)}
                storeSlug={storeSlug}
                customerPhone={user?.phone}
              />
            </>
          ) : (
            <Button
              startIcon={<PersonIcon />}
              onClick={() => setLoginOpen(true)}
              sx={{ color: 'black', textTransform: 'none', fontWeight: 600 }}
            >
              Sign In
            </Button>
          )}

          {/* Cart */}
          <IconButton onClick={() => setCheckoutOpen(true)} sx={{ ml: 2 }}>
            <Badge badgeContent={cartCount} sx={{ '& .MuiBadge-badge': { bgcolor: primaryColor, color: 'white' } }}>
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Banner Carousel */}
      {carouselBanners.length > 0 && (
        <Box sx={{ position: 'relative', width: '100%', bgcolor: '#f5f5f5' }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: isMobile ? '200px' : '400px',
              overflow: 'hidden'
            }}
          >
            {/* Banner Slides */}
            {carouselBanners.map((banner, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: currentBannerIndex === index ? 1 : 0,
                  transition: 'opacity 0.5s ease-in-out',
                  pointerEvents: currentBannerIndex === index ? 'auto' : 'none'
                }}
              >
                {(banner?.url || banner?.imageUrl) ? (
                  banner.type === 'video' ? (
                    <video
                      ref={currentBannerIndex === index ? videoRef : null}
                      src={getImageUrl(banner.url || banner.imageUrl)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      muted
                      autoPlay={currentBannerIndex === index}
                      onEnded={handleVideoEnd}
                    />
                  ) : (
                    <img
                      src={getImageUrl(banner.url || banner.imageUrl)}
                      alt={banner.title || `Banner ${index + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        console.error('Banner image failed to load:', banner);
                        e.target.style.display = 'none';
                      }}
                    />
                  )
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: primaryColor, color: 'white' }}>
                    <Typography variant="h6" fontWeight={700}>{store.storeName}</Typography>
                  </Box>
                )}
              </Box>
            ))}

            {/* Navigation Arrows */}
            {carouselBanners.length > 1 && (
              <>
                <IconButton
                  onClick={prevBanner}
                  sx={{
                    position: 'absolute',
                    left: isMobile ? 8 : 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: '#fff',
                    width: isMobile ? 32 : 44,
                    height: isMobile ? 32 : 44,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                    zIndex: 10
                  }}
                >
                  <NavigateBeforeIcon fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
                <IconButton
                  onClick={nextBanner}
                  sx={{
                    position: 'absolute',
                    right: isMobile ? 8 : 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: '#fff',
                    width: isMobile ? 32 : 44,
                    height: isMobile ? 32 : 44,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                    zIndex: 10
                  }}
                >
                  <NavigateNextIcon fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>

                {/* Dots Indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: isMobile ? 12 : 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 1,
                    zIndex: 10
                  }}
                >
                  {carouselBanners.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentBannerIndex(index)}
                      sx={{
                        width: currentBannerIndex === index ? (isMobile ? 20 : 24) : (isMobile ? 8 : 10),
                        height: isMobile ? 8 : 10,
                        borderRadius: 5,
                        bgcolor: currentBannerIndex === index ? primaryColor : 'rgba(255,255,255,0.5)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 3, pb: isMobile && cartCount > 0 ? 12 : 3 }}>
        {/* Categories */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                size="small"
                onClick={() => setActiveCategory(cat)}
                variant={activeCategory === cat ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: activeCategory === cat ? primaryColor : 'transparent',
                  color: activeCategory === cat ? 'white' : 'inherit',
                  borderColor: activeCategory === cat ? primaryColor : 'rgba(0,0,0,0.23)',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: activeCategory === cat ? primaryColor : `${primaryColor}10`
                  }
                }}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Products Column */}
          <Box sx={{ flex: 1 }}>
            {/* Bestseller Section */}
            {bestsellers.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: primaryColor, borderRadius: 1 }} />
                  <Typography variant="body2" fontWeight={800} sx={{ color: primaryColor }}>
                    🔥 Bestsellers
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: isMobile ? 1.5 : 2
                  }}
                >
                  {bestsellers.map(renderProductCard)}
                </Box>
              </Box>
            )}

            {/* Special Items Section - Matches seller style */}
            {specialItems.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: '#ff9800', borderRadius: 1 }} />
                  <Typography variant="body2" fontWeight={800} sx={{ color: '#ff9800' }}>
                    ⭐ Special Items
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: isMobile ? 1.5 : 2
                  }}
                >
                  {specialItems.slice(0, 6).map(renderProductCard)}
                </Box>
              </Box>
            )}

            {/* Combo Deals Section - Horizontal Scrollable matches seller style */}
            {comboDeals.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: '#9c27b0', borderRadius: 1 }} />
                  <Typography variant="body2" fontWeight={800} sx={{ color: '#9c27b0' }}>
                    🎁 Combo Deals
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 2,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 3 }
                  }}
                >
                  {comboDeals.map((combo) => {
                    const comboId = String(combo.id || combo._id);
                    const isHovered = hoveredProduct === comboId;
                    const isAnimating = animatingProduct === comboId;
                    const qty = cart[comboId]?.qty || 0;
                    
                    // Animation settings for combo
                    const hasAnimation = combo.animation?.enabled && combo.animation?.file;
                    const animationData = hasAnimation ? ANIMATION_MAP[combo.animation.file] : null;
                    const showProductAnimation = hasAnimation && animationData && (
                      combo.animation.trigger === 'always' ||
                      (combo.animation.trigger === 'hover' && isHovered) ||
                      (combo.animation.trigger === 'scroll' && visibleAnimations.has(comboId))
                    );

                    const isFavorite = favorites.has(comboId);

                    return (
                      <Paper
                        key={comboId}
                        data-product-id={comboId}
                        ref={(el) => { if (el) productRefs.current[comboId] = el; }}
                        onMouseEnter={() => setHoveredProduct(comboId)}
                        onMouseLeave={() => setHoveredProduct(null)}
                        onClick={() => addToRecentlyViewed(combo)}
                        sx={{
                          minWidth: 280,
                          maxWidth: 280,
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          position: 'relative',
                          flexShrink: 0,
                          transition: 'transform 0.2s',
                          cursor: 'pointer',
                          '&:hover': { transform: 'translateY(-4px)' }
                        }}
                      >
                        {/* Favorite Button for Combo */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(comboId);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: 'rgba(255,255,255,0.8)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            zIndex: 10,
                            width: 32,
                            height: 32,
                            color: isFavorite ? '#d32f2f' : '#666',
                            '&:hover': { 
                              bgcolor: 'rgba(255,255,255,1)',
                              color: isFavorite ? '#b71c1c' : '#333'
                            }
                          }}
                        >
                          {isFavorite ? <FavoriteIcon sx={{ fontSize: 18 }} /> : <FavoriteBorderIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                        {/* Product Animation Overlay for Combo */}
                        {showProductAnimation && (
                          <Box
                            sx={{
                              position: 'absolute',
                              ...(combo.animation.position === 'top-left' && { top: 8, left: 8 }),
                              ...(combo.animation.position === 'top-right' && { top: 8, right: 8 }),
                              ...(combo.animation.position === 'bottom-left' && { bottom: 8, left: 8 }),
                              ...(combo.animation.position === 'bottom-right' && { bottom: 8, right: 8 }),
                              ...(combo.animation.position === 'center-overlay' && { 
                                top: '50%', 
                                left: '50%', 
                                transform: 'translate(-50%, -50%)' 
                              }),
                              width: combo.animation.size || 80,
                              height: combo.animation.size || 80,
                              zIndex: 5,
                              pointerEvents: 'none'
                            }}
                          >
                            <Lottie
                              animationData={animationData}
                              loop={true}
                              autoplay={true}
                              style={{ width: '100%', height: '100%' }}
                            />
                          </Box>
                        )}

                        {/* Price Badge */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            bgcolor: '#d32f2f',
                            color: '#fff',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontWeight: 800,
                            fontSize: '0.9rem',
                            zIndex: 1,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                        >
                          {currencySymbol} {combo.price.toFixed(2)}
                        </Box>

                        {/* Image */}
                        <Box
                          sx={{
                            height: 140,
                            bgcolor: '#f5f5f5',
                            backgroundImage: combo.imageUrl ? `url(${getImageUrl(combo.imageUrl)})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}
                        />

                        {/* Content */}
                        <Box sx={{ p: 1.5 }}>
                          <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                            {combo.name}
                          </Typography>

                          {/* Product List */}
                          {Array.isArray(combo.productList) && combo.productList.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              {combo.productList.map((item, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 0.5,
                                    borderBottom: idx < combo.productList.length - 1 ? '1px solid #f0f0f0' : 'none'
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#333' }}>
                                    {item.productName || 'Item'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>
                                    {item.qty || 1}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Add Button - Matches seller logic with animation */}
                          {qty > 0 ? (
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ mx: 'auto' }}>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleDecrement(combo); }}
                                sx={{ bgcolor: primaryColor, color: '#fff', width: 24, height: 24, '&:hover': { bgcolor: primaryColor } }}
                              >
                                <RemoveIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              <Typography variant="body1" fontWeight={700} sx={{ minWidth: 30, textAlign: 'center', color: primaryColor }}>
                                {qty}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleIncrement(combo); }}
                                sx={{ bgcolor: primaryColor, color: '#fff', width: 24, height: 24, '&:hover': { bgcolor: primaryColor } }}
                              >
                                <AddIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Stack>
                          ) : (isHovered || isAnimating) ? (
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddToCart(combo);
                              }}
                              sx={{
                                width: 60,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                mx: 'auto'
                              }}
                            >
                              <Lottie
                                lottieRef={isAnimating ? cardLottieRef : undefined}
                                animationData={addToCartAnimation}
                                loop={isAnimating}
                                autoplay={true}
                                style={{ width: 60, height: 60 }}
                              />
                            </Box>
                          ) : (
                            <Button
                              fullWidth
                              size="small"
                              variant="contained"
                              onClick={(e) => { e.stopPropagation(); handleAddToCart(combo); }}
                              sx={{
                                bgcolor: primaryColor,
                                color: '#fff',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                '&:hover': { bgcolor: primaryColor }
                              }}
                            >
                              ADD TO CART
                            </Button>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}


            {/* Recently Viewed Section */}
            {recentlyViewed.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: primaryColor, borderRadius: 1 }} />
                  <Typography variant="body2" fontWeight={800} sx={{ color: primaryColor }}>
                    🕒 Recently Viewed
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: isMobile ? 1.5 : 2
                  }}
                >
                  {recentlyViewed.slice(0, 6).map(renderProductCard)}
                </Box>
              </Box>
            )}

            {/* All Products */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 4, height: 20, bgcolor: primaryColor, borderRadius: 1 }} />
                <Typography variant="body2" fontWeight={800} sx={{ color: primaryColor }}>
                  🍽️ {activeCategory === 'All' ? 'All Items' : activeCategory}
                </Typography>
              </Box>
              
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: isMobile ? 1.5 : 2
                }}
              >
                {filteredProducts.map((product, idx) => (
                  <React.Fragment key={String(product.id || product._id)}>
                    {renderProductCard(product)}

                    {/* Render banners after specific rows - Matches seller logic */}
                    {(idx + 1) % 3 === 0 && (idx + 1) === 3 && renderPlacementBanner('after-row-3')}
                    {(idx + 1) % 6 === 0 && (idx + 1) === 6 && renderPlacementBanner('after-row-6')}
                    {(idx + 1) % 9 === 0 && (idx + 1) === 9 && renderPlacementBanner('after-row-9')}
                  </React.Fragment>
                ))}
              </Box>

              {/* Bottom Placement Banners */}
              {renderPlacementBanner('bottom')}

              {filteredProducts.length === 0 && (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No products found
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right Column - Cart (desktop only) */}
          {!isMobile && (themeConfig.cart.position === 'side' || themeConfig.cart.position === 'side-panel' || themeConfig.cart.position === 'elegant-side' || themeConfig.cart.position === 'fixed-right') && renderCart()}
        </Box>
      </Container>

      {/* Login Dialog */}
      <Dialog open={loginOpen} onClose={() => setLoginOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Sign In / Register
          </Typography>
          <TextField
            fullWidth
            label="Name"
            value={buyer.name}
            onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Phone Number"
            value={buyer.phone}
            onChange={(e) => {
              const phone = e.target.value;
              setBuyer({ ...buyer, phone });
              // Check for existing customer after user stops typing
              if (phone.length >= 10) {
                setTimeout(() => checkExistingCustomer(phone), 500);
              } else {
                setExistingCustomer(null);
              }
            }}
            sx={{ mb: 2 }}
            helperText={checkingCustomer ? 'Checking...' : existingCustomer ? `Welcome back, ${existingCustomer.name}! (${existingCustomer.orderCount} previous orders)` : ''}
            InputProps={{
              endAdornment: existingCustomer && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                </Box>
              )
            }}
          />
          <TextField
            fullWidth
            label="Delivery Location (Address)"
            value={buyer.address}
            onChange={(e) => {
              const newAddress = e.target.value;
              setBuyer(prev => ({ ...prev, address: newAddress }));
              // Auto-detect area from address
              if (newAddress.length > 10 && store?.deliveryAreas) {
                const addressLower = newAddress.toLowerCase();
                const matchedArea = store.deliveryAreas.find(area => {
                  const areaNameLower = area.areaName?.toLowerCase() || '';
                  return areaNameLower && addressLower.includes(areaNameLower);
                });
                if (matchedArea) {
                  setSelectedArea(matchedArea);
                  setBuyer(prev => ({ ...prev, areaCode: matchedArea.areaCode }));
                }
              }
            }}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          
          {/* Delivery Area Selection with Auto-detect */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                Store Area *
              </Typography>
              {store?.deliveryAreas?.length > 0 && (
                <IconButton
                  size="small"
                  onClick={() => {
                    if (!navigator.geolocation) {
                      alert('Geolocation is not supported by your browser');
                      return;
                    }
                    setDetectingLocation(true);
                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        const { latitude, longitude } = position.coords;
                        
                        // Find nearest area
                        let nearest = null;
                        let minDistance = Infinity;
                        let areasWithCoords = 0;
                        
                        store.deliveryAreas.forEach(area => {
                          if (area.latitude && area.longitude) {
                            areasWithCoords++;
                            const R = 6371;
                            const dLat = (area.latitude - latitude) * Math.PI / 180;
                            const dLon = (area.longitude - longitude) * Math.PI / 180;
                            const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                              Math.cos(latitude * Math.PI / 180) * Math.cos(area.latitude * Math.PI / 180) *
                              Math.sin(dLon/2) * Math.sin(dLon/2);
                            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                            const distance = R * c;
                            if (distance < minDistance) {
                              minDistance = distance;
                              nearest = area;
                            }
                          }
                        });
                        
                        if (nearest) {
                          setSelectedArea(nearest);
                          setBuyer(prev => ({ ...prev, areaCode: nearest.areaCode }));
                          alert(`Detected area: ${nearest.areaName} (${minDistance.toFixed(1)} km away)`);
                        } else {
                          if (areasWithCoords === 0) {
                            alert('No delivery areas have GPS coordinates set. Please ask the seller to add coordinates or select area manually.');
                          } else {
                            alert('Could not detect area. Please select manually.');
                          }
                        }
                        setDetectingLocation(false);
                      },
                      (error) => {
                        alert('Failed to get location. Please select manually.');
                        setDetectingLocation(false);
                      }
                    );
                  }}
                  disabled={detectingLocation}
                  title="Detect my location"
                >
                  <MyLocationIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            
            {store?.deliveryAreas?.length > 0 ? (
              <>
                <Autocomplete
                  options={store.deliveryAreas}
                  getOptionLabel={(option) => `${option.areaName} (${option.areaCode})`}
                  value={selectedArea}
                  onChange={(e, newValue) => {
                    setSelectedArea(newValue);
                    setBuyer(prev => ({ ...prev, areaCode: newValue?.areaCode || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select or auto-detect area"
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2">{option.areaName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Code: {option.areaCode}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, value) => option.areaCode === value?.areaCode}
                />
                {selectedArea && (
                  <Chip
                    label={`Selected: ${selectedArea.areaName}`}
                    size="small"
                    color="primary"
                    sx={{ mt: 1 }}
                    onDelete={() => {
                      setSelectedArea(null);
                      setBuyer(prev => ({ ...prev, areaCode: '' }));
                    }}
                  />
                )}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No delivery areas configured
              </Typography>
            )}
          </Box>
          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            disabled={!buyer.name || !buyer.phone || !buyer.areaCode}
            sx={{ bgcolor: primaryColor, '&:hover': { bgcolor: primaryColor } }}
          >
            Continue
          </Button>
        </Box>
      </Dialog>

      {/* Floating Cart Button with Lottie Animation (Fallback for non-themed carts) */}
      {cartCount > 0 && !isMobile && !(themeConfig.cart.position === 'side' || themeConfig.cart.position === 'side-panel' || themeConfig.cart.position === 'elegant-side' || themeConfig.cart.position === 'fixed-right') && (
        <Fab
          onClick={() => setCheckoutOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            bgcolor: primaryColor,
            color: 'white',
            '&:hover': { bgcolor: primaryColor },
            zIndex: 1000,
            width: 64,
            height: 64
          }}
        >
          {showCartAnimation ? (
            <Box sx={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Lottie
                lottieRef={cartLottieRef}
                animationData={addToCartAnimation}
                loop={false}
                autoplay={true}
                style={{ width: 80, height: 80 }}
              />
            </Box>
          ) : (
            <Badge badgeContent={cartCount} sx={{ '& .MuiBadge-badge': { bgcolor: primaryColor, color: 'white' } }}>
              <ShoppingCartIcon sx={{ fontSize: 28 }} />
            </Badge>
          )}
        </Fab>
      )}

      {/* Mobile cart bar - Matches seller view exactly */}
      {isMobile && renderCart()}

      {/* Checkout Dialog */}
      <Dialog 
        open={checkoutOpen} 
        onClose={() => setCheckoutOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '92vh' }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ p: 4, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Typography variant="h5" fontWeight={900}>Confirm Order</Typography>
            <IconButton onClick={() => setCheckoutOpen(false)} size="small">
              <RemoveIcon sx={{ transform: 'rotate(45deg)' }} />
            </IconButton>
          </Box>

          <Box sx={{ px: 4, pb: 2, overflowY: 'auto', minHeight: 0 }}>
            <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" fontWeight={800} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
              Your Items
            </Typography>
            <Stack spacing={2}>
              {Object.values(cart).map((item) => (
                <Box key={item.id || item._id} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: '#f5f5f5',
                      overflow: 'hidden',
                      flexShrink: 0,
                      border: '1px solid #eee'
                    }}
                  >
                    <img
                      src={getImageUrl(item.imageUrl)}
                      alt={item.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight={700}>{item.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currencySymbol}{item.price?.toFixed(2)} × {item.qty}
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={800}>
                    {currencySymbol}{(item.price * item.qty).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Stack>
            </Box>

            <Box sx={{ bgcolor: '#f8f9fa', p: 3, borderRadius: 3, mb: 4, border: '1px solid #eee' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                Delivery To
              </Typography>
              <Button 
                size="small" 
                onClick={() => { setCheckoutOpen(false); setLoginOpen(true); }} 
                sx={{ textTransform: 'none', fontWeight: 700, color: primaryColor }}
              >
                Edit Details
              </Button>
            </Box>
            
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 70 }}>Name:</Typography>
                <Typography variant="body2">{user?.name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 70 }}>Phone:</Typography>
                <Typography variant="body2">{user?.phone}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 100 }}>Store Location:</Typography>
                <Typography variant="body2">
                  {store?.deliveryAreas?.find(a => a.areaCode === user?.areaCode)?.areaName || user?.areaCode}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 100 }}>Delivery Address:</Typography>
                <Typography variant="body2">{user?.address || 'Not specified'}</Typography>
              </Box>
              
              {/* GPS-based distance calculation for per_km and km_range modes */}
              {store?.deliverySettings?.enabled && (store?.deliverySettings?.mode === 'per_km' || store?.deliverySettings?.mode === 'km_range') && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                  {/* Show loading state during auto GPS detection */}
                  {gettingLocation && !gpsAutoFailed && (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: '#f0f9ff', 
                      borderRadius: 2, 
                      border: '1px solid #bae6fd',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="primary">
                        Detecting your location automatically...
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Show calculated distance */}
                  {deliveryDistance > 0 && (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: '#f0f9ff', 
                      borderRadius: 2, 
                      border: '1px solid #bae6fd' 
                    }}>
                      <Typography variant="body2" fontWeight={700} color="primary" sx={{ mb: 0.5 }}>
                        📍 Distance: {deliveryDistance} KM
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {store?.deliverySettings?.mode === 'per_km' 
                          ? `₹${store?.deliverySettings?.perKmCharge || 0} per KM = ₹${deliveryCharge.toFixed(2)}`
                          : `Delivery charge: ₹${deliveryCharge.toFixed(2)}`
                        }
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Show manual button only if auto-detection failed */}
                  {gpsAutoFailed && deliveryDistance === 0 && (
                    <>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={gettingLocation ? <CircularProgress size={18} /> : <MyLocationIcon />}
                        onClick={() => getCustomerLocation(false)}
                        disabled={gettingLocation}
                        sx={{ 
                          mb: 1,
                          textTransform: 'none',
                          borderColor: primaryColor,
                          color: primaryColor,
                          '&:hover': {
                            borderColor: primaryColor,
                            bgcolor: `${primaryColor}10`
                          }
                        }}
                      >
                        {gettingLocation ? 'Getting Location...' : 'Get My Location & Calculate Distance'}
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Automatic detection failed. Click the button above to try again or enable location services.
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Stack>
            </Box>

            {(store?.paymentSettings?.enablePayAtCounter !== false || Boolean(store?.paymentSettings?.enableOnlinePayment)) && (
              <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: 3, mb: 4, border: '1px solid #eee' }}>
                <Typography variant="subtitle2" fontWeight={800} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Payment Method
                </Typography>
                <FormControl>
                  <RadioGroup
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                  >
                    {store?.paymentSettings?.enablePayAtCounter !== false && (
                      <FormControlLabel value="COUNTER" control={<Radio />} label="Pay at Counter" />
                    )}
                    {Boolean(store?.paymentSettings?.enableOnlinePayment) && (
                      <FormControlLabel
                        value="ONLINE"
                        control={<Radio />}
                        label={`Online Payment${store?.paymentSettings?.gatewayProvider ? ` (${store.paymentSettings.gatewayProvider})` : ''}`}
                      />
                    )}
                  </RadioGroup>
                </FormControl>
              </Box>
            )}

            <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" color="text.secondary">Subtotal</Typography>
              <Typography variant="body1" fontWeight={600}>{currencySymbol}{cartSubtotal.toFixed(2)}</Typography>
            </Box>
            {cartTax > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" color="text.secondary">Tax</Typography>
                <Typography variant="body1" fontWeight={600}>{currencySymbol}{cartTax.toFixed(2)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="body1" color="text.secondary">Delivery Fee</Typography>
              <Typography variant="body1" fontWeight={700} sx={{ color: deliveryCharge === 0 ? '#2e7d32' : 'text.primary' }}>
                {deliveryCharge === 0 ? 'FREE' : `${currencySymbol}${deliveryCharge.toFixed(2)}`}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h5" fontWeight={900}>Total Amount</Typography>
              <Typography variant="h5" fontWeight={900} sx={{ color: primaryColor }}>
                {currencySymbol}{cartTotal.toFixed(2)}
              </Typography>
            </Box>
            </Box>
          </Box>

          <Box sx={{ p: 4, pt: 2, flexShrink: 0, borderTop: '1px solid #eee', bgcolor: '#fff' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handlePlaceOrder}
              disabled={Object.keys(cart).length === 0 || placingOrder}
              sx={{ 
                bgcolor: primaryColor, 
                py: 2,
                borderRadius: 3,
                fontWeight: 900,
                fontSize: '1.1rem',
                textTransform: 'none',
                boxShadow: `0 10px 25px ${primaryColor}40`,
                '&:hover': { 
                  bgcolor: primaryColor,
                  boxShadow: `0 15px 30px ${primaryColor}60`,
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s'
              }}
            >
              {placingOrder ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} sx={{ color: '#fff' }} />
                  <span>Processing...</span>
                </Box>
              ) : (
                'Confirm & Place Order'
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Order Receipt Dialog */}
      <PublicStoreReceipt
        open={orderSuccess}
        onClose={() => {
          setOrderSuccess(false);
          setOrderReceipt(null);
        }}
        orderData={orderReceipt}
        storeData={store}
      />

      {/* Floating Favorites Button & Dropdown */}
      {!isMobile && (
        <>
          <Fab
            color="error"
            aria-label="favorites"
            onClick={() => setFavoritesOpen(!favoritesOpen)}
            sx={{
              position: 'fixed',
              right: 24,
              bottom: 100,
              zIndex: 1000,
              boxShadow: '0 4px 20px rgba(211, 47, 47, 0.3)',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: '0 6px 25px rgba(211, 47, 47, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <Badge badgeContent={favorites.size} color="error" max={99}>
              <FavoriteIcon />
            </Badge>
          </Fab>

          {/* Favorites Dropdown Panel */}
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              right: favoritesOpen ? 24 : -400,
              bottom: 170,
              width: 350,
              maxHeight: 500,
              zIndex: 999,
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'right 0.3s ease',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
                color: 'white',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FavoriteIcon />
                <Typography variant="h6" fontWeight={700}>
                  Favorites
                </Typography>
              </Box>
              <Chip
                label={favorites.size}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 700
                }}
              />
            </Box>

            {/* Favorites List */}
            <Box
              sx={{
                maxHeight: 420,
                overflowY: 'auto',
                bgcolor: '#fafafa',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: '#d32f2f',
                  borderRadius: 3
                }
              }}
            >
              {favoritedProducts.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    color: 'text.secondary'
                  }}
                >
                  <FavoriteBorderIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                  <Typography variant="body2">
                    No favorites yet
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Tap the heart icon on products to save them here
                  </Typography>
                </Box>
              ) : (
                favoritedProducts.map((product) => {
                  const productId = String(product.id || product._id);
                  const qty = cart[productId]?.qty || 0;
                  const imgSrc = product.imageUrl
                    ? product.imageUrl.startsWith('http')
                      ? product.imageUrl
                      : `${axios.defaults.baseURL || ''}${product.imageUrl}`
                    : '';

                  return (
                    <Box
                      key={productId}
                      sx={{
                        p: 1.5,
                        borderBottom: '1px solid #e0e0e0',
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        bgcolor: 'white',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: '#fff3f3',
                          transform: 'translateX(-4px)'
                        }
                      }}
                    >
                      {/* Product Avatar */}
                      <Avatar
                        src={imgSrc}
                        alt={product.name}
                        sx={{
                          width: 56,
                          height: 56,
                          border: '2px solid #d32f2f',
                          boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)'
                        }}
                      >
                        {!imgSrc && product.name?.charAt(0)}
                      </Avatar>

                      {/* Product Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.5
                          }}
                        >
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {product.category || 'Others'}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="primary">
                          {currencySymbol}{Number(product.price || 0).toFixed(2)}
                        </Typography>
                      </Box>

                      {/* Actions */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                        {/* Remove from Favorites */}
                        <IconButton
                          size="small"
                          onClick={() => toggleFavorite(productId)}
                          sx={{
                            color: '#d32f2f',
                            '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                          }}
                        >
                          <FavoriteIcon fontSize="small" />
                        </IconButton>

                        {/* Add to Cart */}
                        {qty === 0 ? (
                          <IconButton
                            size="small"
                            onClick={() => handleAddToCart(product)}
                            disabled={!product.isAvailable}
                            sx={{
                              bgcolor: primaryColor,
                              color: 'white',
                              width: 32,
                              height: 32,
                              '&:hover': { bgcolor: primaryColor, opacity: 0.9 }
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <Chip
                            label={qty}
                            size="small"
                            sx={{
                              bgcolor: primaryColor,
                              color: 'white',
                              fontWeight: 700,
                              minWidth: 32,
                              height: 24
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default PublicStorePageEnhanced;
